import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Button, Dropdown } from '@ab-inbev-maz/compass';
import { useCanvasEditor } from './CanvasEditorContext';
import CanvasExportModal from './CanvasExportModal';
import { isTargetLocked, setTargetLocked } from './CanvasLayers';
import type { AutosaveStatus, FlushSave } from '@/hooks/canvas/useCanvasAutosave';

const ZOOM_OPTIONS: { label: string; value: string | number }[] = [50, 75, 90, 100, 125, 150, 200].map((value) => ({
  label: `${value}%`,
  value,
}));
ZOOM_OPTIONS.push({ label: 'Ajustar', value: 'fit' });

export interface CanvasSaveState {
  status: AutosaveStatus;
  lastSavedAt: Date | null;
  isOnline: boolean;
  onSave: () => void;
  // Resolves true once every change is saved.
  onFlush?: FlushSave;
}

interface CanvasToolbarProps {
  title?: string;
  canvasId?: string;
  save?: CanvasSaveState;
}

const RELATIVE_TIME_REFRESH_MS = 30000;
const JUST_NOW_MS = 60000;

const formatSavedAt = (savedAt: Date, now: number) =>
  now - savedAt.getTime() < JUST_NOW_MS
    ? 'Guardado hace un momento'
    : `Guardado ${DateTime.fromJSDate(savedAt).setLocale('es').toRelative({ base: DateTime.fromMillis(now) })}`;

export const getSaveLabel = ({ status, lastSavedAt, isOnline }: Omit<CanvasSaveState, 'onSave' | 'onFlush'>, now: number): string => {
  if (!isOnline && (status === 'dirty' || status === 'error')) return 'Sin conexión, cambios sin guardar';
  if (status === 'saving') return 'Guardando...';
  if (status === 'error') return 'Error al guardar';
  if (status === 'dirty') return 'Cambios sin guardar';
  return lastSavedAt ? formatSavedAt(lastSavedAt, now) : '';
};

// Only this label re-renders every 30s to keep "Guardado hace X" current.
const SaveStatusLabel = ({ save }: { save: CanvasSaveState }) => {
  const [now, setNow] = useState(() => Date.now());
  const showsRelativeTime = !!save.lastSavedAt && (save.status === 'idle' || save.status === 'saved');

  useEffect(() => {
    setNow(Date.now());
    if (!showsRelativeTime) return;
    const interval = setInterval(() => setNow(Date.now()), RELATIVE_TIME_REFRESH_MS);
    return () => clearInterval(interval);
  }, [showsRelativeTime, save.lastSavedAt]);

  const label = getSaveLabel(save, now);
  return (
    <p className={`text-xs ${save.status === 'error' ? 'text-red-600' : 'text-gray-500'}`} aria-live="polite">
      {label}
    </p>
  );
};

// Editor top bar: navigation, save state, zoom and export.
const CanvasToolbar = ({ title = 'Canvas sin título', canvasId, save }: CanvasToolbarProps) => {
  const navigate = useNavigate();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const { canvas, selectedObject, recordHistory, zoom, setZoomLevel, zoomToFit, undo, redo, canUndo, canRedo } =
    useCanvasEditor();

  // Same as the context menu's lock. The canvas's active object is used because, for a multi-selection,
  // selectedObject is only its first element.
  const lockTarget = selectedObject ? (canvas?.getActiveObject() ?? selectedObject) : null;
  const isLocked = !!lockTarget && isTargetLocked(lockTarget);

  const handleToggleLock = () => {
    if (!lockTarget) return;
    setTargetLocked(lockTarget, !isLocked);
    canvas?.requestRenderAll();
    recordHistory(isLocked ? 'Desbloquear elemento' : 'Bloquear elemento');
  };

  const zoomPercent = Math.round(zoom * 100);

  const handleZoomChange = (value: string | number) => {
    if (value === 'fit') {
      zoomToFit();
      return;
    }
    setZoomLevel(Number(value) / 100);
  };

  return (
    <div className="flex items-center justify-between gap-4 px-6 py-2 border-b border-gray-200 bg-zinc-50">
      <div className="flex items-center gap-3">
        <Button
          icon="arrow/arrow-left"
          variant="secondary"
          className="cursor-pointer"
          onClick={() => navigate('/canvas')}
        />
        <div>
          <h1 className="text-base font-bold text-gray-900 leading-tight">{title}</h1>
          {save && <SaveStatusLabel save={save} />}
        </div>
        {save && (
          <Button
            icon="functionalities/save"
            variant="secondary"
            aria-label="Guardar"
            title="Guardar (Ctrl+S)"
            disabled={save.status === 'saving'}
            className="cursor-pointer [--button-border-width:0] [--button-secondary-default-bg:transparent]"
            onClick={save.onSave}
          />
        )}
      </div>

      <div className="flex items-center gap-2">
        <Dropdown
          id="canvas-zoom"
          name="zoom"
          value={zoomPercent}
          placeholder={{ text: `${zoomPercent}%` }}
          options={ZOOM_OPTIONS}
          onChange={handleZoomChange}
          className="w-28 [--form-field-padding-y:0.4rem] [--form-field-padding-x:3rem] [--dropdown-wrapper-border:none] [--dropdown-wrapper-hover-border:none] [--dropdown-right-icon-container-size:1rem]"
        />
        <span className="w-px h-4 bg-gray-400" />

        <Button
          icon="arrow/rotate-cw"
          variant="secondary"
          disabled={!canUndo}
          className="cursor-pointer -scale-x-100 [--button-border-width:0] [--button-secondary-default-bg:transparent]"
          aria-label="Deshacer"
          onClick={undo}
        />

        <span className="w-px h-4 bg-gray-400" />
        <Button
          icon="arrow/rotate-cw"
          variant="secondary"
          disabled={!canRedo}
          className="cursor-pointer [--button-border-width:0] [--button-secondary-default-bg:transparent]"
          aria-label="Rehacer"
          onClick={redo}
        />
        <span className="w-px h-4 bg-gray-400" />
        <Button
          icon={isLocked ? 'functionalities/lock' : 'functionalities/unlock'}
          variant="secondary"
          disabled={!lockTarget}
          className="cursor-pointer disabled:cursor-not-allowed [--button-border-width:0] [--button-secondary-default-bg:transparent]"
          aria-label={isLocked ? 'Desbloquear elemento' : 'Bloquear elemento'}
          title={lockTarget ? (isLocked ? 'Desbloquear elemento' : 'Bloquear elemento') : 'Selecciona un elemento para bloquearlo'}
          onClick={handleToggleLock}
        />
      </div>

      <Button
        label="Exportar"
        rightIcon="arrow/download"
        variant="primary"
        className="cursor-pointer rounded-full"
        onClick={() => setIsExportOpen(true)}
      />

      {isExportOpen && (
        <CanvasExportModal
          title={title}
          canvasId={canvasId}
          flushSave={save?.onFlush}
          onClose={() => setIsExportOpen(false)}
        />
      )}
    </div>
  );
};

export default CanvasToolbar;
